import { UserWithGroup } from '@app/core/types/users';
import { useQueryParam, UserRole } from '@mi-front/core';
import { LinkItem } from '@mi-front/ui';
import { MessageDescriptor, defineMessage, useIntl } from 'react-intl';

export enum TabKey {
  INFO = 'info',
  PERMISSIONS = 'permissions',
  ACTIONS_PERMISSIONS = 'actions-permissions',
}

const tabMessages = {
  info: defineMessage({
    id: 'admin.users.viewScreen.basicInfoTab',
    defaultMessage: 'Info',
  }),
  stationsPermissions: defineMessage({
    id: 'admin.users.viewScreen.stationsPermissionsTab',
    defaultMessage: 'Acceso a emisoras',
  }),
  actionsPermissions: defineMessage({
    id: 'admin.users.viewScreen.actionsPermissionsTab',
    defaultMessage: 'Permisos',
  }),
};

/**
 * Returns which tab keys are available for the given user role.
 */
function getAvailableTabs(user: UserWithGroup): TabKey[] {
  const permissionsEnabled = user.role === UserRole.CLIENT_USER;
  return permissionsEnabled
    ? [TabKey.INFO, TabKey.PERMISSIONS, TabKey.ACTIONS_PERMISSIONS]
    : [TabKey.INFO, TabKey.PERMISSIONS];
}

/**
 * Builds header nav link items for the user view page.
 */
export function buildAdminUsersViewTabs(
  user: UserWithGroup,
  $t: (descriptor: MessageDescriptor) => string,
): LinkItem[] {
  const basePath = `/admin/users/${user.id}`;
  const permissionsEnabled = user.role === UserRole.CLIENT_USER;

  const tabs: LinkItem[] = [
    {
      to: basePath,
      text: $t(tabMessages.info),
      isActive: (_match, location) => {
        const tab = new URLSearchParams(location.search).get('tab');
        return !tab || tab === TabKey.INFO;
      },
    },
    {
      to: `${basePath}?tab=${TabKey.PERMISSIONS}`,
      text: $t(tabMessages.stationsPermissions),
      isActive: (_match, location) =>
        new URLSearchParams(location.search).get('tab') === TabKey.PERMISSIONS,
    },
  ];

  if (permissionsEnabled) {
    tabs.push({
      to: `${basePath}?tab=${TabKey.ACTIONS_PERMISSIONS}`,
      text: $t(tabMessages.actionsPermissions),
      isActive: (_match, location) =>
        new URLSearchParams(location.search).get('tab') === TabKey.ACTIONS_PERMISSIONS,
    });
  }

  return tabs;
}

/**
 * Builds header nav tabs for the user view and resolves the active tab from `?tab=`.
 * The actions-permissions tab is only included for CLIENT_USER roles.
 */
export function useAdminUsersViewTabs(user: UserWithGroup): {
  tabs: LinkItem[];
  activeTab: TabKey;
} {
  const { $t } = useIntl();
  const tabParam = useQueryParam('tab');
  const availableTabs = getAvailableTabs(user);
  const tabs = buildAdminUsersViewTabs(user, $t);
  const activeTab = availableTabs.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : TabKey.INFO;

  return { tabs, activeTab };
}
